import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from './utils'

// ─── Button ───────────────────────────────────────────────────────────────────

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'accent'
  size?:    'sm' | 'md' | 'lg'
  loading?: boolean
  icon?:    ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', loading, icon, children, className, disabled, ...props }, ref) => {
    const base = 'inline-flex items-center justify-center gap-2 font-semibold rounded-xl transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-room-accent focus-visible:ring-offset-2 focus-visible:ring-offset-room-bg disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.97]'

    const variants = {
      primary:   'bg-room-accent hover:bg-[#c026d3] text-white shadow-lg shadow-room-accent/20',
      secondary: 'bg-room-surface border border-room-border hover:border-room-accent text-room-text hover:text-white',
      ghost:     'text-room-muted hover:text-room-text hover:bg-room-surface',
      danger:    'bg-room-error/10 border border-room-error/30 hover:bg-room-error/20 text-room-error',
      accent:    'bg-room-accent/10 border border-room-accent/40 hover:bg-room-accent/20 text-room-accent',
    }

    const sizes = {
      sm: 'text-xs px-3 py-1.5',
      md: 'text-sm px-4 py-2.5',
      lg: 'text-base px-6 py-3.5',
    }

    return (
      <button
        ref={ref}
        className={cn(base, variants[variant], sizes[size], className)}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? <Loader2 size={size === 'sm' ? 12 : 16} className="animate-spin" /> : icon}
        {children}
      </button>
    )
  }
)
Button.displayName = 'Button'

// ─── Input ────────────────────────────────────────────────────────────────────

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?:  string
  error?:  string
  hint?:   string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, className, ...props }, ref) => (
    <div className="space-y-1.5">
      {label && <label className="text-xs text-room-text font-medium">{label}</label>}
      <input
        ref={ref}
        className={cn(
          'w-full bg-room-surface border rounded-xl px-4 py-3 text-white placeholder-room-muted text-sm',
          'focus:outline-none focus:ring-2 focus:ring-room-accent focus:border-transparent transition-all',
          error ? 'border-room-error' : 'border-room-border hover:border-room-muted',
          className
        )}
        {...props}
      />
      {error && <p className="text-xs text-room-error">{error}</p>}
      {hint && !error && <p className="text-xs text-room-muted">{hint}</p>}
    </div>
  )
)
Input.displayName = 'Input'

// ─── Select ───────────────────────────────────────────────────────────────────

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
}
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, className, children, ...props }, ref) => (
    <div className="space-y-1.5">
      {label && <label className="text-xs text-room-text font-medium">{label}</label>}
      <select
        ref={ref}
        className={cn(
          'w-full bg-room-surface border border-room-border rounded-xl px-4 py-3 text-white text-sm',
          'focus:outline-none focus:ring-2 focus:ring-room-accent focus:border-transparent transition-all',
          'hover:border-room-muted',
          className
        )}
        {...props}
      >
        {children}
      </select>
    </div>
  )
)
Select.displayName = 'Select'

// ─── Card ─────────────────────────────────────────────────────────────────────

interface CardProps { children: ReactNode; className?: string; glow?: boolean }

export function Card({ children, className, glow }: CardProps) {
  return (
    <div className={cn(
      'bg-room-surface border border-room-border rounded-2xl p-5',
      glow && 'animate-glow',
      className
    )}>
      {children}
    </div>
  )
}

// ─── Badge ────────────────────────────────────────────────────────────────────

type BadgeVariant = 'default' | 'success' | 'error' | 'warning' | 'gold' | 'accent'

interface BadgeProps { children: ReactNode; variant?: BadgeVariant; className?: string }

export function Badge({ children, variant = 'default', className }: BadgeProps) {
  const variants: Record<BadgeVariant, string> = {
    default: 'bg-room-border   text-room-text',
    success: 'bg-emerald-900/40 text-emerald-300',
    error:   'bg-red-900/40    text-red-300',
    warning: 'bg-orange-900/40 text-orange-300',
    gold:    'bg-amber-900/40  text-amber-300',
    accent:  'bg-room-accent/20 text-room-accent',
  }
  return (
    <span className={cn('inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full', variants[variant], className)}>
      {children}
    </span>
  )
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

export function Spinner({ size = 20, className }: { size?: number; className?: string }) {
  return <Loader2 size={size} className={cn('animate-spin text-room-accent', className)} />
}

// ─── Progress bar ─────────────────────────────────────────────────────────────

export function ProgressBar({ value, className }: { value: number; className?: string }) {
  return (
    <div className={cn('w-full h-1.5 bg-room-border rounded-full overflow-hidden', className)}>
      <div
        className="h-full bg-gradient-to-r from-room-accent to-purple-400 rounded-full transition-all duration-300"
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  )
}

// ─── Toggle ──────────────────────────────────────────────────────────────────

interface ToggleProps { checked: boolean; onChange: (value: boolean) => void; label?: string }

export function Toggle({ checked, onChange, label }: ToggleProps) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <div
        onClick={() => onChange(!checked)}
        className={cn(
          'w-10 h-6 rounded-full transition-colors relative shrink-0',
          checked ? 'bg-room-accent' : 'bg-room-border'
        )}
      >
        <span className={cn(
          'absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform',
          checked ? 'translate-x-5' : 'translate-x-1'
        )} />
      </div>
      {label && <span className="text-sm text-room-text">{label}</span>}
    </label>
  )
}

// ─── Divider ──────────────────────────────────────────────────────────────────

export function Divider({ label }: { label?: string }) {
  if (!label) return <hr className="border-room-border" />
  return (
    <div className="flex items-center gap-3">
      <hr className="flex-1 border-room-border" />
      <span className="text-xs text-room-muted">{label}</span>
      <hr className="flex-1 border-room-border" />
    </div>
  )
}
